import { useState, useEffect } from 'react'
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import parsePhoneNumber, {
  getCountries,
  getCountryCallingCode,
  AsYouType,
  formatIncompletePhoneNumber
} from 'libphonenumber-js/min'
import { InputGroup, Input, InputLeftElement, Text, useFormControlContext } from '~/design_system'

import VndlySelect from '~/components/VndlySelect'
import { isClient } from '~/common/nextJsUtils'
import { useMsg } from '~/common/localizationUtils'
import { useIntlContext } from '~/components/IntlProviders.context'

function _isPossibleNumber(contactNumber) {
  const phoneDigits = contactNumber?.match(/\d+/g)?.join('')
  return phoneDigits && phoneDigits.length >= 5 && phoneDigits.length <= 15
}

export const getCountriesList = () => {
  const countriesList = getCountries()
    .map(country => {
      return {
        label: `${getUnicodeFlagIcon(country)} ${country} +${getCountryCallingCode(country)}`,
        value: country
      }
    })
    .filter(country => country.value !== 'US')
  // Add US to the top of the list
  countriesList.unshift({
    label: `${getUnicodeFlagIcon('US')} ${'US'} +${getCountryCallingCode('US')}`,
    value: 'US'
  })
  return countriesList
}

const _initializeValue = (contactNumber, country) => {
  let formatted
  const parsed = parsePhoneNumber(contactNumber, country)
  if (_isPossibleNumber(contactNumber) && parsed) {
    formatted = parsed.formatNational()
  } else {
    const formatter = new AsYouType(country)
    formatter.input(contactNumber)
    formatted = formatter.getNationalNumber()
  }

  return formatIncompletePhoneNumber(formatted, country)
}

export const formatChangedInput = (newValue, prevValue, country) => {
  let newInternational
  let newCountry
  let newNational
  // check for null or undefined and replace with default
  const contactNumber = newValue ?? ''
  // check for null and undefined and replace with default
  const inputCountry = country ?? 'US'
  if (contactNumber) {
    const parsed = parsePhoneNumber(contactNumber, inputCountry)
    if (_isPossibleNumber(contactNumber) && parsed) {
      newNational = parsed.formatNational()
      newInternational = parsed.number
      if (contactNumber.length > 0 && contactNumber[0] === '+') newCountry = parsed.country
    } else {
      newNational = formatIncompletePhoneNumber(contactNumber, inputCountry)
      newInternational = `+${getCountryCallingCode(inputCountry)}${contactNumber}`

      // Hack to delete formatted area code
      if (prevValue === newNational && prevValue.length > newValue.length) {
        if (newNational.indexOf(prevValue) === 0) {
          // Trim the last digit (or plus sign).
          newInternational = newInternational.slice(0, -1)
          newNational = newNational.slice(0, -1)
        }
      }
    }
  }

  return {
    newCountry,
    newInternational: newInternational ?? '',
    newNational: newNational ?? ''
  }
}

const PhoneNumberField = props => {
  const msg = useMsg()
  const {
    id: idProp,
    country,
    onChange,
    onCountryChange,
    placeholder = msg('common.ui.enter_phone_number'),
    value,
    formContext,
    uiSchema,
    ...rest
  } = props
  const [countrySelectList, setCountrySelectList] = useState()
  // check for null or undefined and replace with default
  const inputValue = value ?? ''
  // check for null and undefined and replace with default
  const inputCountry = country ?? 'US'
  const national = _initializeValue(inputValue, inputCountry)
  const { id: formControlId } = useFormControlContext() ?? {}
  const id = idProp ?? formControlId

  function _onChangeInput(e) {
    const newValue = e.target.value
    const { newCountry, newInternational } = formatChangedInput(newValue, national, country)
    if (newCountry) {
      onCountryChange(newCountry)
    }
    onChange(newInternational)
  }

  function _onChangeCountry(option) {
    const selectedCountry = option.value
    onCountryChange(selectedCountry)

    const international = `+${getCountryCallingCode(selectedCountry)}${national}`
    onChange(international)
  }

  useEffect(() => {
    setCountrySelectList(getCountriesList())
  }, [])

  const selectedOption = countrySelectList ? countrySelectList.find(opts => opts.value === inputCountry) : ''
  return formContext?.displayAsText || uiSchema?.['ui:displayAsText'] ? (
    <Text>{selectedOption && national ? `${selectedOption.label} ${national}` : '-'}</Text>
  ) : (
    <InputGroup>
      <InputLeftElement minH="38px" width="160px" backgroundColor="white" borderColor="#cccccc">
        <VndlySelect
          id={id + '_country_select'}
          // HACK: style is deprecated but it works better on non local env - Feb 10, 2020 Tom C
          style={{ minWidth: '160px' }}
          options={countrySelectList}
          value={selectedOption}
          onChange={_onChangeCountry}
          menuPortalTarget={isClient() ? document.body : undefined}
        />
      </InputLeftElement>
      <Input
        id={id}
        type="tel"
        color="#59636F"
        minH="38px"
        pl="170px"
        borderRadius="0"
        borderLeft="0"
        fontSize="14px"
        focusBorderColor="brand.500"
        borderColor="#cccccc"
        onChange={_onChangeInput}
        value={national}
        placeholder={placeholder}
        maxLength="15"
        data-testid="phone-number-field-input"
        {...rest}
      />
    </InputGroup>
  )
}

const UncontrolledPhoneNumberField = props => {
  const msg = useMsg()
  const locale = new Intl.Locale(useIntlContext().intl.locale)
  const {
    key = 'phone-number-input',
    defaultCountry,
    onChange,
    onCountryChange,
    placeholder = msg('common.ui.enter_phone_number'),
    defaultContactNumber,
    name = 'contact-number'
  } = props
  // set up local state (formatting phone number, set country selected, etc)
  const [country, setCountry] = useState(locale?.region || defaultCountry || 'US')
  const [countrySelectList, setCountrySelectList] = useState()
  const initialNumber = _initializeValue(defaultContactNumber, country)
  const [contactNumber, setContactNumber] = useState(initialNumber || '')

  function _onChangeInput(e) {
    const newValue = e.target.value
    const { newCountry, newInternational, newNational } = formatChangedInput(newValue, contactNumber, country)
    if (newCountry) {
      setCountry(newCountry)
      onCountryChange(newCountry)
    }

    setContactNumber(newNational)
    onChange(newInternational)
  }

  function _onChangeCountry(option) {
    const selectedCountry = option.value
    setCountry(selectedCountry)
    onCountryChange(selectedCountry)

    const parsed = parsePhoneNumber(contactNumber, selectedCountry)
    let national
    let international
    if (_isPossibleNumber(contactNumber) && parsed) {
      national = parsed.formatNational()
      international = parsed.number
    } else {
      national = formatIncompletePhoneNumber(contactNumber, selectedCountry)
      international = `+${getCountryCallingCode(selectedCountry)}${contactNumber}`
    }

    setContactNumber(national ?? '')
    onChange(international ?? '')
  }

  useEffect(() => {
    setCountrySelectList(getCountriesList())
  }, [])
  const selectedOption = countrySelectList ? countrySelectList.find(opts => opts.value === country) : ''
  return (
    <div key={key}>
      <InputGroup>
        <InputLeftElement minH="38px" width="160px" backgroundColor="white" borderColor="#cccccc">
          <VndlySelect
            // HACK: style is deprecated but it works better on non local env - Feb 10, 2020 Tom C
            style={{ minWidth: '160px', fontSize: '14px' }}
            options={countrySelectList}
            value={selectedOption}
            onChange={_onChangeCountry}
          />
        </InputLeftElement>
        <Input
          name={name}
          type="tel"
          color="#59636F"
          minH="38px"
          pl="170px"
          fontSize="14px"
          borderColor="#cccccc"
          borderRadius="0"
          borderLeft="0"
          focusBorderColor="brand.500"
          onChange={_onChangeInput}
          value={contactNumber}
          placeholder={placeholder}
          maxLength="15"
        />
      </InputGroup>
    </div>
  )
}

export default PhoneNumberField

export { UncontrolledPhoneNumberField }
